import { useEffect, useMemo, useState, useRef } from "react";
import { Box, TextField, Typography, ToggleButton, ToggleButtonGroup, FormControl, Select, MenuItem, Alert, IconButton, Button, Checkbox, FormControlLabel } from "@mui/material";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { convertDateTimeToInputDateString } from "../../utils/datetimeUtils";
import handleAgentName from "../../utils/userUtils";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { formatPhoneNumber } from "../../utils/phoneUtils";
import { getAllCities } from "../../api/cityApi";
import { getAddons, getPricingHistories, getPricingHistoryItems, getPricing } from "../../api/resourceApi";
import { computeSizing } from "../../utils/rscSizing";
import { methodBreakdown, termForMethod } from "../../utils/ccPricing";

// CC = cash ÷ factor, rounded UP to RM 10 (latest rates 2026-06-17).
const CC_FACTORS_GF = { 12: 0.95, 24: 0.93, 36: 0.915, 60: 0.88 };
const ccFromCashGF = (cash, term) => {
    const c = Number(cash) || 0;
    if (c <= 0) return 0;
    return Math.ceil(c / (CC_FACTORS_GF[term] || 0.915) / 10) * 10;
};

const sectionTitle = (text) => (
    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 2.5, mb: 1.5, pb: 0.5, borderBottom: '1px solid #E2E8F0', fontFamily: "'Poppins', sans-serif" }}>
        {text}
    </Typography>
);

const fieldLabel = (text, required) => (
    <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
        {text} {required && <span style={{ color: '#EF4444' }}>*</span>}
    </Typography>
);

export default function GenericSalesForm({
    mode = "create",
    isSuperAdmin = false,
    agentGroup, agentName, agentNameOptions = [], selectedAgentId, setSelectedAgentId,
    coBrokeGroupName, coBrokeAgentName,
    coBrokeGroupOptions = [], coBrokeNameOptions = [],
    selectedCoBrokeGroupId, selectedCoBrokeAgentId, setSelectedCoBrokeAgentId,
    // Co-broke fields can be unlocked independently of the main agent (e.g. the
    // admin "Adjustment Edit": agent stays locked, co-broke becomes editable).
    // Defaults fall back to the agent flags so create-sales is unchanged.
    disableCoBrokeFields, handleCoBrokeGroupChange,
    referralOptions = [], selectedReferralId, setSelectedReferralId, disableReferralField = false,
    customerName, setCustomerName, customerEmail, setCustomerEmail,
    customerPhone, setCustomerPhone, customerAddress, setCustomerAddress,
    customerState, setCustomerState, customerCity, setCustomerCity, customerPostcode, setCustomerPostcode,
    selectedCategory, setSelectedCategory, phaseMode = 'single', setPhaseMode = () => {}, panelQty, setPanelQty, energy, setEnergy,
    availablePaymentMethods = [], paymentMethod, setPaymentMethod,
    originalPrice, setOriginalPrice, canEditPrice = false, residentialMatch,
    // Edit-sales "edit-in-place" two-box mode (privileged editor only):
    //   Original Sales Price = the payment-method/stored basis that DRIVES COMMISSION
    //     (originalPrice here holds the stored value directly — saved without conversion).
    //   Original Cash Price (ocp) = ATAP-list reference; warns if OSP ≠ derive(OCP).
    // Default off → create-sales / RSC keep the single cash box + conversion-on-save.
    salesPriceLens = false, ocp = '', setOcp,
    finalPrice, addOnPrice, setAddOnPrice, depositOn, setDepositOn,
    asternDiscount, setAsternDiscount, agentDiscount, setAgentDiscount,
    discountFundedBy, setDiscountFundedBy,
    agentDiscountContribution, setAgentDiscountContribution,
    uplineDiscountContribution, setUplineDiscountContribution,
    disableAgentFields = false, disableCustomerFields = false,
    disableProductFields = false, disablePricingFields = false,
    disableDepositDate = false,
    // RSC-style promo auto-discount (manual create/edit only)
    showPromos = false,
    promoBackupbox = false, setPromoBackupbox,
    promoFirst640 = false, setPromoFirst640,
    promoPerBatt = false, setPromoPerBatt,
    handleAgentGroupChange,
    // Add-on table
    lineItems = [], setLineItems, canEditAddons = false,
    // Saved (Section-2) pricing for an existing sale — the method-basis values that
    // were stored at create. Shown read-only when Section 1 (cash) is left blank on
    // edit, so a sale's payment-method pricing REMAINS visible after creation.
    savedPricing = null, savedPaymentMethod,
    // RSC-style sizing validity — called with the sizing verdict so the parent can
    // block create/edit when the config is invalid (over-SEDA without CCC, or the
    // safety ratio is breached), exactly like the RSC calculator gates generation.
    onSizingChange,
    // Lock the promo checkboxes (read-only). The edit page sets this true once a sale
    // is Active/signed (price frozen) and false while Pending (still adjustable).
    promosLocked = false,
    // Seed the panel-wattage dropdown (UI-only). Used by the RSC → Create-Sales
    // hand-off so the RSC package's wattage carries over. Defaults to 640 when unset.
    initialPanelWattage,
}) {
    const inputSx = { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' };
    const PP = "'Poppins', sans-serif";

    // True only after the user DELIBERATELY toggles a promo checkbox in this session.
    // Gates every promo-driven auto-calc below so they NEVER fire on mount/edit-load —
    // that would clobber a sale's frozen Astern discount / battery price (price-freeze
    // rule). See feedback_price_freeze_no_retroactive_atap.
    // In CREATE mode the promos are active (default-ticked) from the start, so allow
    // their auto-fill/row effects to run immediately — there is no frozen sale price
    // to protect. In EDIT mode it stays false until the user deliberately toggles a
    // promo (price-freeze rule).
    const promoTouchedRef = useRef(mode === 'create');

    // ── Add-on catalog ────────────────────────────────────────────────
    const [addonCatalog, setAddonCatalog] = useState([]);
    useEffect(() => {
        getAddons()
            .then(r => setAddonCatalog(r.data || []))
            .catch(() => {});
    }, []);
    // Backfill catalog descriptions onto line items that were reloaded from the DB
    // without one (SalesLineItem doesn't persist the description) so the quotation
    // still renders the description bullets. One-shot, name-matched.
    const descBackfilledRef = useRef(false);
    useEffect(() => {
        if (descBackfilledRef.current || !setLineItems) return;
        if (!(addonCatalog || []).length || !(lineItems || []).length) return;
        let changed = false;
        const next = lineItems.map(li => {
            if (li.isBattery) return li;
            const cat = addonCatalog.find(a => (a.itemName || '').trim() === (li.itemName || '').trim());
            if (!cat) return li;
            let out = li;
            // Restore the description (SalesLineItem doesn't persist it).
            if (cat.description && !li.description) { out = { ...out, description: cat.description }; changed = true; }
            // Restore the base+rate split (priceBase/priceRate aren't persisted) so the
            // base box + per-unit rate show. Keep the FROZEN line total: rate comes from
            // the catalog, base = storedTotal − rate×qty (so the total never changes).
            const ps = String(cat.price ?? '').replace(/[^\d.+]/g, '');
            if (ps.includes('+') && !(Number(out.priceRate) > 0)) {
                const rate = Number(ps.split('+')[1]) || 0;
                const q = Number(out.qty) || 0;
                const total = Number(out.lineTotal) || 0;
                const base = +(total - rate * q).toFixed(2);
                out = { ...out, priceRate: rate, priceBase: base, unitPrice: rate };
                changed = true;
            }
            return out;
        });
        descBackfilledRef.current = true;
        if (changed) setLineItems(next);
    }, [addonCatalog, lineItems, setLineItems]);

    // Pricing-version validity note: match the sale's deposit date to the pricing
    // history version that was active then (ValidFrom ≤ depositOn ≤ ValidUntil).
    const [pricingHistories, setPricingHistories] = useState([]);
    useEffect(() => {
        getPricingHistories().then(r => setPricingHistories(r.data || [])).catch(() => {});
    }, []);
    const activePricing = useMemo(() => {
        if (!(pricingHistories || []).length) return null;
        // Match against the deposit date when set; otherwise fall back to the
        // latest price list (the version active today) so the note always maps
        // to a price list even before a deposit date is entered.
        const dep = depositOn ? new Date(depositOn) : new Date();
        dep.setHours(0, 0, 0, 0);
        if (isNaN(dep.getTime())) return null;
        const match = pricingHistories.find(h => {
            const from = new Date(h.validFrom); from.setHours(0, 0, 0, 0);
            const until = h.validUntil ? new Date(h.validUntil) : null;
            if (until) until.setHours(0, 0, 0, 0);
            return dep >= from && (!until || dep <= until);
        });
        if (match) return match;
        // No window contains the date — fall back to the most recent list by
        // ValidFrom so a sale dated past the last range still shows the latest.
        return [...pricingHistories]
            .sort((a, b) => new Date(b.validFrom) - new Date(a.validFrom))[0] || null;
    }, [depositOn, pricingHistories]);
    const fmtPriceDate = (d) => new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    // The current/latest price list = the one with the most recent ValidFrom. It is
    // always treated as effective "to present" (a stale ValidUntil shouldn't make the
    // live list look expired), and dated from when it was created.
    const latestHistoryId = useMemo(() => {
        if (!(pricingHistories || []).length) return null;
        return [...pricingHistories].sort((a, b) => new Date(b.validFrom) - new Date(a.validFrom))[0]?.historyId ?? null;
    }, [pricingHistories]);
    // Pull the price list ITEMS for the version active on the deposit date, so the
    // reference uses the price that was valid then (not the live price).
    const [activeItems, setActiveItems] = useState([]);
    useEffect(() => {
        if (activePricing?.historyId) {
            getPricingHistoryItems(activePricing.historyId).then(r => setActiveItems(r.data || [])).catch(() => setActiveItems([]));
        } else {
            setActiveItems([]);
        }
    }, [activePricing]);
    // Cash price (single/3p) from the deposit-date version, matched by panel count.
    const histRefCash = useMemo(() => {
        if (!activeItems.length || !panelQty) return 0;
        const row = activeItems.find(i => (i.listName || '').toLowerCase().includes('residential') && Number(i.panelQty) === Number(panelQty));
        if (!row) return 0;
        return phaseMode === '3phase'
            ? (Number(row.cash3p8k) || Number(row.cashPrice) || 0)
            : (Number(row.cashPrice) || Number(row.cash3p8k) || 0);
    }, [activeItems, panelQty, phaseMode]);

    // LIVE Residential price list — used so the Price Reference matches what RSC / the
    // current price table actually charge (the deposit-date history snapshot can drift
    // stale when the live list is edited without re-snapshotting the history version).
    const [livePricing, setLivePricing] = useState([]);
    useEffect(() => {
        getPricing().then(r => setLivePricing((r.data || []).filter(p => (p.listName || '').toLowerCase().includes('residential')))).catch(() => {});
    }, []);
    const liveRefCash = useMemo(() => {
        if (!livePricing.length || !panelQty) return 0;
        const row = livePricing.find(p => Number(p.panelQty) === Number(panelQty));
        if (!row) return 0;
        return phaseMode === '3phase'
            ? (Number(row.cash3p8k) || Number(row.cashPrice) || 0)
            : (Number(row.cashPrice) || Number(row.cash3p8k) || 0);
    }, [livePricing, panelQty, phaseMode]);

    // Compute add-on total from line items and keep addOnPrice in sync — but ONLY
    // when there ARE line items. Old sales (no line items) carry a lump-sum
    // Add-on Price that must be PRESERVED, not overwritten to 0; the agent edits
    // that field manually instead.
    const hasLineItems = (lineItems || []).length > 0;
    const addonTotal = useMemo(
        () => (lineItems || []).reduce((sum, r) => sum + (Number(r.lineTotal) || 0), 0),
        [lineItems]
    );
    // Once a sale is line-item-driven, keep addOnPrice in sync — INCLUDING dropping it
    // to 0 when the last line item is removed (otherwise it stayed stale and the removed
    // battery's price lingered). Sales that never had line items (old lump-sum) keep
    // their manually-entered Add-on Price untouched.
    const everHadLineItems = useRef(false);
    useEffect(() => { if (hasLineItems) everHadLineItems.current = true; }, [hasLineItems]);
    useEffect(() => {
        if (setAddOnPrice && (hasLineItems || everHadLineItems.current)) setAddOnPrice(addonTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addonTotal, hasLineItems]);

    // helpers for editing rows
    const updateRow = (idx, patch) => {
        if (!setLineItems) return;
        setLineItems(prev => {
            const next = prev.map((r, i) => {
                if (i !== idx) return r;
                const merged = { ...r, ...patch };
                const q = Number(merged.qty) || 0;
                if ('priceBase' in patch || 'priceRate' in patch) {
                    // Edit of the base box or the per-unit rate on a base+rate add-on:
                    // unitPrice = rate; total = base + rate × qty.
                    merged.unitPrice = Number(merged.priceRate) || 0;
                    merged.lineTotal = +((Number(merged.priceBase) || 0) + (Number(merged.priceRate) || 0) * q).toFixed(2);
                } else if ('unitPrice' in patch) {
                    // Manual unit-price edit on a FLAT item → drop any base+rate.
                    merged.priceBase = 0; merged.priceRate = 0;
                    merged.lineTotal = +(q * (Number(merged.unitPrice) || 0)).toFixed(2);
                } else if (Number(merged.priceRate) > 0) {
                    // qty change on a base+rate add-on (Optimizer "700 base + 170/pc"):
                    // unitPrice stays the per-pc RATE; total = base + rate × qty.
                    merged.unitPrice = Number(merged.priceRate);
                    merged.lineTotal = +(Number(merged.priceBase || 0) + Number(merged.priceRate) * q).toFixed(2);
                } else {
                    merged.lineTotal = +(q * (Number(merged.unitPrice) || 0)).toFixed(2);
                }
                return merged;
            });
            return next;
        });
    };
    const removeRow = (idx) => {
        if (!setLineItems) return;
        setLineItems(prev => prev.filter((_, i) => i !== idx));
    };
    const addFromCatalog = (addonId) => {
        if (!setLineItems || !addonId) return;
        const addon = addonCatalog.find(a => String(a.addonId) === String(addonId));
        if (!addon) return;
        // Parse price — may be "700+170" (base+rate, e.g. Optimizer RM700 + RM170/pc)
        // or plain "1500". For base+rate keep base + rate so changing qty (pcs) does
        // base + rate×qty (matches the RSC), not a flat unit × qty.
        const priceStr = String(addon.price ?? '').replace(/[^\d.+]/g, '');
        let priceBase = 0, priceRate = 0, unitPrice = 0;
        if (priceStr.includes('+')) {
            const [base, rate] = priceStr.split('+');
            priceBase = Number(base) || 0;
            priceRate = Number(rate) || 0;
            unitPrice = priceRate;   // show the per-unit RATE (base is added once, via lineTotal)
        } else {
            unitPrice = Number(priceStr) || 0;
        }
        const qty = 1;
        const lineTotal = priceRate > 0 ? +(priceBase + priceRate * qty).toFixed(2) : +(qty * unitPrice).toFixed(2);
        setLineItems(prev => [
            ...prev,
            {
                salesLineItemId: 0,
                itemName: addon.itemName,
                category: addon.category || null,
                description: addon.description || '',   // shown as bullets on the quotation
                isBattery: false,
                qty,
                batteryKwh: null,
                priceBase,
                priceRate,
                unitPrice,
                lineTotal,
                sortOrder: prev.length,
            },
        ]);
    };

    // Battery add-on: ATAP-priced 16 kWh modules @ RM8200 each (true 16 kWh ATAP
    // cash price), up to 10×16 = 160 kWh. Commercial sales adjust the unit price
    // manually on the line (commercial batteries are priced per-kWh, not flat).
    const BATTERY_UNIT_KWH = 16, BATTERY_UNIT_PRICE = 8200, PER_BATTERY_DISCOUNT = 800;
    const batteryKwhOptions = Array.from({ length: 10 }, (_, i) => (i + 1) * BATTERY_UNIT_KWH); // 16..160
    const [batteryKwhSel, setBatteryKwhSel] = useState('');
    // Net per-16kWh-battery price: base less the RM800 per-battery promo when on.
    const battNetPrice = (showPromos && promoPerBatt) ? (BATTERY_UNIT_PRICE - PER_BATTERY_DISCOUNT) : BATTERY_UNIT_PRICE;
    // Every battery is a single 16 kWh module; the selected total kWh just decides
    // how many modules (qty). The line is always named "Battery 16kWh" so the name
    // stays correct when the qty is edited. Adding more increments the qty.
    const addBattery = (kwh) => {
        const k = Number(kwh) || 0;
        if (!setLineItems || k <= 0) return;
        const units = Math.max(1, Math.ceil(k / BATTERY_UNIT_KWH));
        setLineItems(prev => {
            const idx = prev.findIndex(r => r.isBattery);
            if (idx >= 0) {
                const next = [...prev];
                const newQty = (Number(next[idx].qty) || 0) + units;
                next[idx] = {
                    ...next[idx],
                    qty: newQty,
                    unitPrice: battNetPrice,
                    lineTotal: +(newQty * battNetPrice).toFixed(2),
                };
                return next;
            }
            return [...prev, {
                salesLineItemId: 0,
                itemName: `Battery ${BATTERY_UNIT_KWH}kWh`,
                category: 'Battery',
                isBattery: true,
                qty: units,
                batteryKwh: BATTERY_UNIT_KWH,
                unitPrice: battNetPrice,
                lineTotal: +(units * battNetPrice).toFixed(2),
                sortOrder: prev.length,
            }];
        });
        setBatteryKwhSel('');
    };

    // RSC-style auto-discount: compute Astern discount from the promos (phase- and
    // battery-aware) whenever they change. Manual create/edit only (showPromos).
    useEffect(() => {
        if (!showPromos || !setAsternDiscount || !promoTouchedRef.current) return;
        const is3p = phaseMode === '3phase';
        const hasBattery = (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0);
        const boxRemoved = promoBackupbox && !hasBattery;
        let astern = 0;
        if (boxRemoved) astern += is3p ? 2500 : 1600;
        if (promoFirst640) astern += boxRemoved ? (is3p ? 1600 : 800) : (is3p ? 3000 : 1800);
        setAsternDiscount(astern);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPromos, promoBackupbox, promoFirst640, phaseMode, lineItems]);

    // Per-battery RM800 promo: fold into the battery line's unit price (16kWh only).
    useEffect(() => {
        if (!showPromos || !setLineItems || !promoTouchedRef.current) return;
        setLineItems(prev => {
            let changed = false;
            const next = prev.map(r => {
                if (r.isBattery && Number(r.batteryKwh) === 16 && Number(r.unitPrice) !== battNetPrice) {
                    changed = true;
                    return { ...r, unitPrice: battNetPrice, lineTotal: +(battNetPrice * (Number(r.qty) || 0)).toFixed(2) };
                }
                return r;
            });
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPromos, promoPerBatt]);

    // Backup-Box add-on — REMOVAL MARKER ONLY (mirrors the RSC fix, user 2026-06-08):
    // the system base price ALREADY includes the backup box, so it must NEVER be
    // added as a priced line. The row exists ONLY when Backup-Box Removal is ticked
    // (no battery) — qty 0 / price-for-reference, documenting it was removed; the
    // −RM1,600/2,500 lives in the Astern-discount auto-fill. Box included normally
    // (removal unticked) → NO row. Battery present → no row (bundle includes it).
    useEffect(() => {
        if (!showPromos || !setLineItems || selectedCategory !== 'RESIDENTIAL' || !promoTouchedRef.current) return;
        const is3p = phaseMode === '3phase';
        const bbName = is3p ? 'Three Phase Backup-Box (Removed)' : 'Single Phase Backup-Box (Removed)';
        const bbPrice = is3p ? 2500 : 1600;   // reference price; qty 0 keeps it out of totals
        setLineItems(prev => {
            const list = prev || [];
            const hasBattery = list.some(li => li.isBattery && Number(li.qty) > 0);
            const others = list.filter(r => !r._isBackup);
            // Marker only when removal ticked AND no battery; otherwise no backup row.
            const needRow = promoBackupbox && !hasBattery;
            const next = needRow
                ? [...others, {
                    salesLineItemId: 0, itemName: bbName, category: 'Battery', isBattery: false,
                    qty: 0, unitPrice: bbPrice, lineTotal: 0,
                    _isBackup: true, sortOrder: others.length,
                }]
                : others;
            return JSON.stringify(list) === JSON.stringify(next) ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPromos, promoBackupbox, phaseMode, selectedCategory, lineItems]);

    // Battery present → the bundle includes the backup box, so any Backup-Box
    // "(Removed)" marker must disappear. Runs UNGATED (also for edited/reloaded sales
    // that never toggled a promo) — matches the bundle rule the user confirmed.
    useEffect(() => {
        if (!setLineItems) return;
        const hasBattery = (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0);
        if (!hasBattery) return;
        setLineItems(prev => {
            const next = (prev || []).filter(r => !r._isBackup && !/backup-box\s*\(removed\)/i.test(r.itemName || ''));
            return next.length === (prev || []).length ? prev : next;
        });
    }, [lineItems, setLineItems]);

    // ── RSC-style sizing / SEDA / CCC validation ─────────────────────────────
    // Same rules as the RSC calculator: read the matched row's inverter, check the
    // SEDA cap (1P 5kW / 3P 15kW) and the safety ratio, and require a CCC add-on
    // when the inverter is over-SEDA. A CCC line item (RM1,000) is detected by name.
    const CCC_RE = /\bccc\b|connection\s*confirmation/i;
    const cccPresent = (lineItems || []).some(li => CCC_RE.test(li.itemName || ''));
    const hasBatteryLine = (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0);
    const sizing = useMemo(() => {
        if (selectedCategory !== 'RESIDENTIAL') return null;
        return computeSizing({
            energy, phaseMode, inverterRow: residentialMatch,
            hasBattery: hasBatteryLine, cccPresent,
            lineItems, addonCatalog,
        });
    }, [selectedCategory, energy, phaseMode, residentialMatch, hasBatteryLine, cccPresent, lineItems, addonCatalog]);
    // Surface the verdict to the parent (for the create/edit submit gate).
    useEffect(() => {
        if (onSizingChange) onSizingChange(sizing);
    }, [sizing, onSizingChange]);
    // The CCC catalog add-on (RM1,000 SEDA registration).
    const cccAddon = useMemo(
        () => (addonCatalog || []).find(a => CCC_RE.test(a.itemName || '')),
        [addonCatalog]
    );
    const addCccRow = () => {
        if (!setLineItems) return;
        if ((lineItems || []).some(li => CCC_RE.test(li.itemName || ''))) return; // already present
        // Prefer the real catalog add-on — add it through the SAME parser the catalog
        // picker uses (base+rate aware, e.g. "1000+0" → RM1,000, not RM10,000).
        if (cccAddon) { addFromCatalog(cccAddon.addonId); return; }
        // Fallback only if no CCC row exists in the add-on catalog: synthetic RM1,000.
        setLineItems(prev => [...(prev || []), {
            salesLineItemId: 0, itemName: 'CCC (SEDA Registration)', category: 'CCC',
            isBattery: false, qty: 1, batteryKwh: null,
            priceBase: 0, priceRate: 0, unitPrice: 1000,
            lineTotal: 1000, sortOrder: (prev || []).length,
        }]);
    };

    // Agent Discount accepts NEGATIVES (= mark-up). A plain number input snaps a lone
    // "-" back to 0 (parseFloat("-") is NaN), so we keep a string mirror and only push
    // a valid number to the parent — letting the user type "-500" naturally.
    const [agentDiscStr, setAgentDiscStr] = useState(
        (agentDiscount === '' || agentDiscount == null) ? '' : String(agentDiscount)
    );
    const agentDiscSyncRef = useRef(agentDiscount);
    useEffect(() => {
        if (agentDiscount !== agentDiscSyncRef.current) {
            agentDiscSyncRef.current = agentDiscount;
            setAgentDiscStr((agentDiscount === '' || agentDiscount == null) ? '' : String(agentDiscount));
        }
    }, [agentDiscount]);
    const onAgentDiscInput = (raw) => {
        if (!/^-?\d*\.?\d*$/.test(raw)) return;   // digits, one dot, optional leading "-"
        setAgentDiscStr(raw);
        const n = (raw === '' || raw === '-' || raw === '.' || raw === '-.') ? 0 : parseFloat(raw);
        const val = isNaN(n) ? 0 : n;
        agentDiscSyncRef.current = val;
        if (setAgentDiscount) setAgentDiscount(val);
    };

    // Apply the suggested inverter upgrade: add each upgrade add-on (via the catalog
    // parser) + CCC if the target exceeds SEDA (mirrors RSC's upgrade pill).
    const applyInverterUpgrade = (upgrade) => {
        if (!setLineItems || !upgrade) return;
        (upgrade.addonIds || []).forEach(id => {
            const already = (lineItems || []).some(li => {
                const cat = (addonCatalog || []).find(a => String(a.addonId) === String(id));
                return cat && (li.itemName || '').trim() === (cat.itemName || '').trim();
            });
            if (!already) addFromCatalog(id);
        });
        if (upgrade.overSeda) addCccRow();
    };

    // Panel wattage is UI-only (not persisted). Default to 640W.
    const panelWattageOptions = ['550', '580', '600', '620', '640', '650'];
    const [panelWattage, setPanelWattage] = useState(initialPanelWattage ? String(initialPanelWattage) : '640');
    // RSC hand-off: when the seed prop arrives after mount, adopt it (without
    // recomputing energy — the kWp is already prefilled directly). Only fires when
    // the prop actually changes, so a user's manual wattage choice is never clobbered.
    useEffect(() => {
        if (initialPanelWattage) setPanelWattage(String(initialPanelWattage));
    }, [initialPanelWattage]);
    const wattMultiplier = +(parseInt(panelWattage) / 1000).toFixed(2);

    const handleWattageChange = (val) => {
        if (!val) return;
        setPanelWattage(val);
        const mult = +(parseInt(val) / 1000).toFixed(2);
        if (panelQty > 0) {
            setEnergy(Number((panelQty * mult).toFixed(2)));
        }
    };

    // ── State + City reference data ────────────────────────────────
    const [cities, setCities] = useState([]);
    useEffect(() => {
        let cancelled = false;
        getAllCities()
            .then(res => { if (!cancelled) setCities(res.data || []); })
            .catch(err => console.error('Failed to load cities:', err));
        return () => { cancelled = true; };
    }, []);
    const stateOptions = useMemo(() => {
        const set = new Set();
        cities.forEach(c => { if (c.state) set.add(c.state); });
        return Array.from(set).sort();
    }, [cities]);
    const cityOptionsForState = useMemo(() => {
        if (!customerState) return [];
        return cities
            .filter(c => c.state === customerState)
            .map(c => c.cityName)
            .sort();
    }, [cities, customerState]);
    const customerFieldsLocked = disableCustomerFields && !isSuperAdmin;
    // Co-broke editability defaults to the main-agent lock unless explicitly overridden.
    const _coBrokeDisabled = disableCoBrokeFields ?? disableAgentFields;
    const _onCoBrokeGroupChange = handleCoBrokeGroupChange || handleAgentGroupChange;

    return (
        <>
            {/* Agent Information */}
            {sectionTitle('Agent Information')}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Agent Group')}
                    <TextField size="small" fullWidth disabled value={agentGroup || ""} InputProps={{ sx: inputSx }} />
                </Box>
                <Box>
                    {fieldLabel('Agent Name')}
                    {disableAgentFields ? (
                        <TextField size="small" fullWidth disabled value={agentName || ""} InputProps={{ sx: inputSx }} />
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select value={selectedAgentId || ''} onChange={e => setSelectedAgentId(e.target.value)} sx={inputSx}>
                                {agentNameOptions.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1 }}>
                <Box>
                    {fieldLabel('Co-broke Group')}
                    {_coBrokeDisabled ? (
                        <TextField size="small" fullWidth disabled value={coBrokeGroupName || ""} InputProps={{ sx: inputSx }} />
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select value={selectedCoBrokeGroupId || ''} onChange={_onCoBrokeGroupChange} sx={inputSx}>
                                <MenuItem value="">—</MenuItem>
                                {coBrokeGroupOptions.map((o, i) => <MenuItem key={i} value={o.groupId}>{o.groupName}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                </Box>
                <Box>
                    {fieldLabel('Co-Agent (Offline)')}
                    {_coBrokeDisabled ? (
                        <TextField size="small" fullWidth disabled value={coBrokeAgentName || ""} InputProps={{ sx: inputSx }} />
                    ) : (
                        <FormControl size="small" fullWidth>
                            <Select disabled={!selectedCoBrokeGroupId} value={selectedCoBrokeAgentId || ''} onChange={e => setSelectedCoBrokeAgentId(e.target.value)} sx={inputSx}>
                                <MenuItem value="">—</MenuItem>
                                {coBrokeNameOptions.map(o => <MenuItem key={o.userId} value={o.userId}>{handleAgentName(o.firstName, o.lastName, o.alias)}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </Box>

            {/* Referral picker — optional 3rd-party Referral-role user. Filtered to
                Referral-role only and excludes the Agent / CoAgent on the form. */}
            {setSelectedReferralId && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1 }}>
                    <Box>
                        {fieldLabel('Referral (Optional)')}
                        <FormControl size="small" fullWidth>
                            <Select
                                value={selectedReferralId || ''}
                                onChange={e => setSelectedReferralId(e.target.value)}
                                disabled={disableReferralField}
                                sx={inputSx}
                                displayEmpty
                            >
                                <MenuItem value="">—</MenuItem>
                                {referralOptions
                                    .filter(o => String(o.userId) !== String(selectedAgentId)
                                              && String(o.userId) !== String(selectedCoBrokeAgentId))
                                    .map(o => {
                                        const composed = handleAgentName(o.firstName, o.lastName, o.alias);
                                        const display = (composed && composed.trim()) || o.username || `User #${o.userId}`;
                                        return (
                                            <MenuItem key={o.userId} value={o.userId}>
                                                {display}
                                            </MenuItem>
                                        );
                                    })}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
            )}

            {/* Customer Information */}
            {sectionTitle('Customer Information')}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Customer Name', true)}
                    <TextField size="small" fullWidth required value={customerName || ''} onChange={e => setCustomerName(e.target.value)} disabled={disableCustomerFields && !isSuperAdmin} InputProps={{ sx: inputSx }} />
                </Box>
                <Box>
                    {fieldLabel('Phone Number', true)}
                    <TextField size="small" fullWidth required placeholder="012-345 6789" value={customerPhone || ''} onChange={e => setCustomerPhone(formatPhoneNumber(e.target.value))} disabled={disableCustomerFields && !isSuperAdmin} InputProps={{ sx: inputSx }} />
                </Box>
            </Box>
            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Email')}
                <TextField size="small" fullWidth type="email" value={customerEmail || ''} onChange={e => setCustomerEmail(e.target.value)} disabled={disableCustomerFields && !isSuperAdmin} InputProps={{ sx: inputSx }} />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('State', true)}
                    <FormControl size="small" fullWidth required>
                        <Select
                            value={customerState || ''}
                            onChange={e => {
                                if (setCustomerState) setCustomerState(e.target.value);
                                if (setCustomerCity) setCustomerCity('');
                            }}
                            disabled={customerFieldsLocked}
                            displayEmpty
                            sx={inputSx}
                        >
                            <MenuItem value=""><em>Select state…</em></MenuItem>
                            {stateOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    {fieldLabel('City', true)}
                    <FormControl size="small" fullWidth required>
                        <Select
                            value={customerCity || ''}
                            onChange={e => setCustomerCity && setCustomerCity(e.target.value)}
                            disabled={customerFieldsLocked || !customerState}
                            displayEmpty
                            sx={inputSx}
                        >
                            <MenuItem value=""><em>{customerState ? 'Select city…' : 'Select state first'}</em></MenuItem>
                            {cityOptionsForState.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Postcode')}
                <TextField size="small" fullWidth value={customerPostcode || ''}
                    onChange={e => setCustomerPostcode && setCustomerPostcode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="e.g. 11900"
                    disabled={customerFieldsLocked}
                    InputProps={{ sx: inputSx }} />
            </Box>
            <Box sx={{ mb: 1 }}>
                {fieldLabel('Address (Street / No. / Jalan)', true)}
                <TextField size="small" fullWidth multiline rows={2} value={customerAddress || ''} onChange={e => setCustomerAddress(e.target.value)} disabled={customerFieldsLocked} InputProps={{ sx: inputSx }} placeholder="No. 12, Jalan Ampang…" />
            </Box>

            {/* Product Information */}
            {sectionTitle('Product Information')}
            {/* Category + Phase share one row (Phase = Residential only) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: selectedCategory === 'RESIDENTIAL' ? '1fr 1fr' : '1fr' }, gap: 1.5, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Product Category', true)}
                    <ToggleButtonGroup
                        value={selectedCategory}
                        exclusive
                        onChange={(e, val) => val && setSelectedCategory(val)}
                        disabled={disableProductFields}
                        fullWidth
                        size="small"
                        sx={{ '& .MuiToggleButton-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', py: 0.6, '&.Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)' } } } }}
                    >
                        <ToggleButton value="RESIDENTIAL">Residential</ToggleButton>
                        <ToggleButton value="COMMERCIAL">Commercial</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
                {selectedCategory === 'RESIDENTIAL' && (
                    <Box>
                        {fieldLabel('Phase')}
                        <ToggleButtonGroup
                            value={phaseMode}
                            exclusive
                            onChange={(e, val) => val && setPhaseMode(val)}
                            disabled={disableProductFields}
                            fullWidth
                            size="small"
                            sx={{ '& .MuiToggleButton-root': { fontFamily: "'Poppins', sans-serif", fontSize: '0.8rem', fontWeight: 600, textTransform: 'none', py: 0.6, '&.Mui-selected': { background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', color: 'white', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)' } } } }}
                        >
                            <ToggleButton value="single">Single Phase</ToggleButton>
                            <ToggleButton value="3phase">3 Phase</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr' }, gap: 1.25, mb: 1 }}>
                <Box>
                    {fieldLabel('Panel Wattage')}
                    <FormControl size="small" fullWidth>
                        <Select
                            value={panelWattage}
                            onChange={e => handleWattageChange(e.target.value)}
                            disabled={disableProductFields}
                            sx={inputSx}
                        >
                            {panelWattageOptions.map(w => (
                                <MenuItem key={w} value={w}>{w} W</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box>
                    {fieldLabel('Number of Panels', true)}
                    <TextField size="small" fullWidth type="number" value={panelQty || ''} disabled={disableProductFields}
                        onWheel={e => e.target.blur()}
                        onChange={e => { const qty = parseInt(e.target.value); setPanelQty(qty); setEnergy(Number((qty * wattMultiplier).toFixed(2))); }}
                        InputProps={{ sx: inputSx, inputProps: { min: 1, step: 1 } }} />
                </Box>
                <Box>
                    {fieldLabel('System Size (kWp)')}
                    <TextField size="small" fullWidth disabled value={energy || '0.00'} InputProps={{ sx: inputSx }} />
                    <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', mt: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                        Calculated: {panelQty} panels × {wattMultiplier} kWp
                    </Typography>
                </Box>
            </Box>

            {/* RSC-style sizing verdict: inverter / ratio / SEDA-CCC. Only a VALID
                config (ratio safe + CCC when over-SEDA) can be created — the parent
                blocks submit on sizing.valid === false. */}
            {sizing && (
                <Box sx={{ mb: 1.5, p: 1, borderRadius: 1.5, border: '1px solid', borderColor: sizing.valid ? '#BBF7D0' : '#FECACA', bgcolor: sizing.valid ? '#F0FDF4' : '#FEF2F2', fontFamily: PP }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', fontSize: '0.72rem' }}>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color: '#0F172A', fontWeight: 700 }}>
                            {sizing.phaseDigit}P {sizing.upgraded
                                ? <><span style={{ textDecoration: 'line-through', opacity: 0.5, marginRight: 4 }}>{sizing.naturalKw}kW</span>{sizing.inverterKw}kW</>
                                : `${sizing.inverterKw}kW`}
                        </span>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color: sizing.ratioOk ? '#16A34A' : '#D97706', fontWeight: 600 }}>
                            ratio {sizing.ratio.toFixed(2)} / {sizing.ratioCap}
                        </span>
                        <span style={{ color: '#64748B' }}>SEDA {sizing.sedaCap}kW</span>
                    </Box>
                    {!sizing.ratioOk && (
                        <Box sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: '0.7rem', color: '#B45309' }}>{sizing.reason}</Typography>
                            {canEditAddons && (sizing.batteryResolves || sizing.upgrade) && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.4 }}>
                                    {sizing.batteryResolves && (
                                        <Button size="small" variant="text" onClick={() => addBattery(16)}
                                            sx={{ fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#16A34A', bgcolor: '#DCFCE7', px: 1, py: 0.25, borderRadius: '6px', '&:hover': { bgcolor: '#BBF7D0' } }}>
                                            + Add 16kWh Battery
                                        </Button>
                                    )}
                                    {sizing.upgrade && (
                                        <Button size="small" variant="text" onClick={() => applyInverterUpgrade(sizing.upgrade)}
                                            sx={{ fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#B45309', bgcolor: '#FEF3C7', px: 1, py: 0.25, borderRadius: '6px', '&:hover': { bgcolor: '#FDE68A' } }}>
                                            ⬆ Upgrade inverter {sizing.phaseDigit}P {sizing.upgrade.targetKw}kW{sizing.upgrade.overSeda ? ' & CCC' : ''} +RM{sizing.upgrade.cost.toLocaleString()}
                                        </Button>
                                    )}
                                </Box>
                            )}
                            {sizing.upgrade?.overSeda && (
                                <Typography sx={{ fontSize: '0.62rem', color: '#B91C1C', mt: 0.4 }}>
                                    SEDA max {sizing.upgrade.sedaMax}kW — the upgrade also adds CCC (RM1,000) to register.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {sizing.ratioOk && sizing.cccRequired && (
                        <Box sx={{ mt: 0.5 }}>
                            {sizing.cccPresent ? (
                                <Typography sx={{ fontSize: '0.7rem', color: '#16A34A', fontWeight: 600 }}>
                                    ✓ CCC added — {sizing.inverterKw}kW SEDA-registered
                                </Typography>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#B91C1C', mb: 0.4 }}>{sizing.reason}</Typography>
                                    {canEditAddons && (
                                        <Button size="small" variant="text" onClick={addCccRow}
                                            sx={{ fontFamily: PP, fontSize: '0.68rem', fontWeight: 700, textTransform: 'none', color: '#B91C1C', bgcolor: '#FEE2E2', px: 1, py: 0.25, borderRadius: '6px', '&:hover': { bgcolor: '#FECACA' } }}>
                                            + Add CCC RM1,000
                                        </Button>
                                    )}
                                </>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Payment Method', true)}
                <FormControl size="small" fullWidth>
                    <Select value={paymentMethod || ''} onChange={e => setPaymentMethod(e.target.value)} disabled={disableProductFields || disablePricingFields || availablePaymentMethods.length === 0} sx={inputSx}>
                        <MenuItem value="">Select Payment Method</MenuItem>
                        {availablePaymentMethods.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {/* Price Reference (display only) — the customer price WITHOUT the agent
                discount: cash + CC by formula, plus the pricing-version validity.
                Cash basis = matched package cash (or the entered method-price converted
                back to cash), so it's correct for any payment method. */}
            {(() => {
                const pm = String(paymentMethod || '').toLowerCase();
                const ccFactor = pm.includes('60') ? 0.88 : pm.includes('36') ? 0.915 : pm.includes('24') ? 0.93 : pm.includes('12') ? 0.95 : 1;
                const matchCash = residentialMatch
                    ? ((phaseMode === '3phase' ? (Number(residentialMatch.cash3p8k) || Number(residentialMatch.cashPrice)) : (Number(residentialMatch.cashPrice) || Number(residentialMatch.cash3p8k))) || 0)
                    : 0;
                // Pure SYSTEM original price for this panel count (the price-list row,
                // NOT net of discounts and NOT incl add-ons). Use the LIVE list for the
                // current/latest version (so it matches RSC + can't go stale when the
                // list is edited without re-snapshotting the history). Only a genuinely
                // BACK-DATED sale (deposit date in an older history version) uses that
                // version's frozen snapshot price.
                const isLatestRef = !activePricing || activePricing.historyId === latestHistoryId;
                const refCash = isLatestRef
                    ? (liveRefCash > 0 ? liveRefCash : (histRefCash > 0 ? histRefCash : (matchCash > 0 ? matchCash : Math.round((Number(originalPrice) || 0) * ccFactor))))
                    : (histRefCash > 0 ? histRefCash : (liveRefCash > 0 ? liveRefCash : (matchCash > 0 ? matchCash : Math.round((Number(originalPrice) || 0) * ccFactor))));
                if (refCash <= 0) return null;
                return (
                    <Box sx={{ mb: 1.5, p: 1, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 1.5, fontFamily: "'Poppins', sans-serif" }}>
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#1A8CFF', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.4 }}>Price Reference - Original Price</Typography>
                        {/* Always show the full reference — cash + every CC term — so the
                            agent sees all options regardless of the selected method. */}
                        <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.74rem' }}>
                            <span style={{ minWidth: 70, color: '#1A8CFF' }}>Cash</span>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: '#0F172A' }}>RM {refCash.toLocaleString()}</span>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1.5, fontSize: '0.7rem', mt: 0.25 }}>
                            <span style={{ minWidth: 70, color: '#1A8CFF', whiteSpace: 'nowrap' }}>Credit Card</span>
                            <span style={{ fontFamily: "'JetBrains Mono',monospace", color: '#0F172A' }}>12M RM {ccFromCashGF(refCash, 12).toLocaleString()} · 24M RM {ccFromCashGF(refCash, 24).toLocaleString()} · 36M RM {ccFromCashGF(refCash, 36).toLocaleString()} · 60M RM {ccFromCashGF(refCash, 60).toLocaleString()}</span>
                        </Box>
                        {activePricing && (() => {
                            // Latest list → "created date to present"; older lists keep their real range.
                            const isLatest = activePricing.historyId === latestHistoryId;
                            const fromDate = isLatest ? (activePricing.createdOn || activePricing.validFrom) : activePricing.validFrom;
                            const toLabel = (isLatest || !activePricing.validUntil) ? 'present' : fmtPriceDate(activePricing.validUntil);
                            return (
                                <Typography sx={{ fontSize: '0.66rem', color: '#1A8CFF', mt: 0.4, fontWeight: 600 }}>
                                    ℹ {activePricing.name || 'Price list'} · valid {fmtPriceDate(fromDate)} to {toLabel}
                                </Typography>
                            );
                        })()}
                    </Box>
                );
            })()}
            {salesPriceLens ? (() => {
                // Two-box edit-in-place. Original Sales Price = stored value (commission
                // basis), edited + saved DIRECTLY. Original Cash Price = ATAP reference;
                // warn (don't block) when OSP doesn't match the cash-derived figure.
                const _term = termForMethod(paymentMethod);                 // 0 = cash
                const _isMix = String(paymentMethod || '').toLowerCase().includes('mix');
                // OCP follows the ATAP DATED-list reference — the SAME source the blue
                // Price-Reference box above uses (liveRefCash / histRefCash by deposit
                // date), NOT residentialMatch (which is often null on the edit page →
                // gave "list RM 0" and a bogus 44,820 warning). residentialMatch is only
                // a last-ditch fallback.
                const _isLatestRef = !activePricing || activePricing.historyId === latestHistoryId;
                const _listRef = (_isLatestRef
                    ? (liveRefCash > 0 ? liveRefCash : (histRefCash > 0 ? histRefCash : 0))
                    : (histRefCash > 0 ? histRefCash : (liveRefCash > 0 ? liveRefCash : 0)))
                    || (residentialMatch
                        ? ((phaseMode === '3phase' ? (Number(residentialMatch.cash3p8k) || Number(residentialMatch.cashPrice)) : (Number(residentialMatch.cashPrice) || Number(residentialMatch.cash3p8k))) || 0)
                        : 0);
                const _effOcp = (ocp !== '' && ocp != null && Number(ocp) > 0) ? Number(ocp) : _listRef;
                const _derivedOsp = _isMix ? null : (_term > 0 ? ccFromCashGF(_effOcp, _term) : _effOcp);
                const _mismatch = _derivedOsp != null && _effOcp > 0 && Math.abs((Number(originalPrice) || 0) - _derivedOsp) > 10;
                return (
                    <Box sx={{ mb: 1.5 }}>
                        {_mismatch && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.6 }}>
                                <WarningAmberRounded sx={{ fontSize: '0.95rem', color: '#B45309', mt: '1px', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.68rem', color: '#B45309', fontFamily: PP, lineHeight: 1.3 }}>
                                    Original Sales Price (RM {Number(originalPrice).toLocaleString()}) differs from the ATAP {_term > 0 ? `${_term}M CC` : 'cash'} reference RM {_derivedOsp.toLocaleString()} (list RM {_listRef.toLocaleString()}) — likely a manual price. Verify it and note the reason in Remarks.
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                            <Box>
                                {fieldLabel('Original Sales Price (RM)', true)}
                                <TextField size="small" fullWidth type="number" disabled={!canEditPrice}
                                    value={originalPrice || ''} onChange={e => setOriginalPrice(parseFloat(e.target.value) || 0)}
                                    placeholder="Enter sales price"
                                    helperText="Saved / quotation / commission basis"
                                    InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                            </Box>
                            <Box>
                                {fieldLabel('Original Cash Price (Ori)')}
                                <TextField size="small" fullWidth type="number" disabled={!canEditPrice}
                                    value={(ocp !== '' && ocp != null) ? ocp : (_listRef || '')}
                                    onChange={e => setOcp && setOcp(parseFloat(e.target.value) || 0)}
                                    placeholder="ATAP list reference"
                                    helperText={_isMix ? 'Mix — reference only (no derive)' : (_listRef > 0 ? `ATAP list: RM ${_listRef.toLocaleString()} — derives the sales price` : 'ATAP reference — derives the sales price')}
                                    InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                            </Box>
                        </Box>
                    </Box>
                );
            })() : (
                <Box sx={{ mb: 1.5 }}>
                    {fieldLabel('Original Price (RM)', true)}
                    <TextField size="small" fullWidth type="number" disabled={!canEditPrice}
                        value={originalPrice || ''} onChange={e => setOriginalPrice(parseFloat(e.target.value) || 0)}
                        placeholder={canEditPrice ? "Enter price" : "Auto-populated"}
                        InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                </Box>
            )}

            {/* RSC-style promos that auto-fill the Astern discount. On EDIT they are
                LOCKED (read-only) — they show what was applied at the sale but never
                recompute the frozen discount; edit the Astern Discount field directly. */}
            {showPromos && (selectedCategory === 'RESIDENTIAL') && !disablePricingFields && (() => {
                return (
                <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    {fieldLabel(promosLocked ? 'Promotions (applied at sale — locked)' : 'Promotions (auto-fill Astern discount)')}
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <FormControlLabel
                            disabled={promosLocked || (lineItems || []).some(li => li.isBattery && Number(li.qty) > 0)}
                            control={<Checkbox size="small" checked={!!promoBackupbox} onChange={e => { promoTouchedRef.current = true; setPromoBackupbox && setPromoBackupbox(e.target.checked); }} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>Backup-Box Removal (RM1,600 / RM2,500 — no battery only)</Typography>}
                        />
                        <FormControlLabel
                            disabled={promosLocked}
                            control={<Checkbox size="small" checked={!!promoFirst640} onChange={e => { promoTouchedRef.current = true; setPromoFirst640 && setPromoFirst640(e.target.checked); }} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>FIRST640 Promo (RM1,800 / RM3,000)</Typography>}
                        />
                        <FormControlLabel
                            disabled={promosLocked || !(lineItems || []).some(li => li.isBattery && Number(li.qty) > 0)}
                            control={<Checkbox size="small" checked={!!promoPerBatt} onChange={e => { promoTouchedRef.current = true; setPromoPerBatt && setPromoPerBatt(e.target.checked); }} />}
                            label={<Typography sx={{ fontFamily: PP, fontSize: '0.82rem' }}>Per-Battery RM800 off (16kWh — folds into battery price)</Typography>}
                        />
                    </Box>
                    {promosLocked && <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', fontFamily: PP, mt: 0.5 }}>Locked — change the Astern Discount field directly to adjust.</Typography>}
                </Box>
                );
            })()}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 1.5 }}>
                <Box>
                    {fieldLabel('Astern Discount (RM)')}
                    <TextField size="small" fullWidth type="number" value={asternDiscount ?? ''} onWheel={e => e.target.blur()}
                        onChange={e => setAsternDiscount(parseFloat(e.target.value) || 0)} disabled={disablePricingFields}
                        placeholder="0.00" InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }}
                        helperText={showPromos ? 'Auto-filled from promotions above — editable' : undefined}
                        FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }} />
                </Box>
                <Box>
                    {fieldLabel('Agent Discount (RM)')}
                    <TextField size="small" fullWidth type="text" inputMode="decimal" value={agentDiscStr} onWheel={e => e.target.blur()}
                        onChange={e => onAgentDiscInput(e.target.value)} disabled={disablePricingFields}
                        placeholder="0.00 (use − for mark-up)" InputProps={{ sx: inputSx }}
                        helperText="Negative = mark-up"
                        FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }} />
                </Box>
            </Box>

            {/* Discount Funded By — only show when agent discount > 0 and in create mode */}
            {agentDiscount > 0 && setDiscountFundedBy && (
                <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    {fieldLabel('Discount Funded By')}
                    <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                        <Select value={discountFundedBy || 'Own'} onChange={e => {
                            setDiscountFundedBy(e.target.value);
                            if (e.target.value === 'Own') { setAgentDiscountContribution(agentDiscount); setUplineDiscountContribution(0); }
                            else if (e.target.value === 'CompanyUpline') { setAgentDiscountContribution(0); setUplineDiscountContribution(agentDiscount); }
                        }} sx={inputSx}>
                            <MenuItem value="Own">My Own</MenuItem>
                            <MenuItem value="CompanyUpline">Company & Upline</MenuItem>
                            <MenuItem value="Split">Split</MenuItem>
                        </Select>
                    </FormControl>
                    {discountFundedBy === 'Split' && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <Box>
                                {fieldLabel('My Contribution (RM)')}
                                <TextField size="small" fullWidth type="number" value={agentDiscountContribution ?? ''} onWheel={e => e.target.blur()}
                                    onChange={e => { const v = parseFloat(e.target.value) || 0; setAgentDiscountContribution(v); setUplineDiscountContribution(Math.max(0, (agentDiscount || 0) - v)); }}
                                    InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                            </Box>
                            <Box>
                                {fieldLabel('Company & Upline Contribution (RM)')}
                                <TextField size="small" fullWidth type="number" value={uplineDiscountContribution ?? ''} onWheel={e => e.target.blur()}
                                    onChange={e => { const v = parseFloat(e.target.value) || 0; setUplineDiscountContribution(v); setAgentDiscountContribution(Math.max(0, (agentDiscount || 0) - v)); }}
                                    InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }} />
                            </Box>
                        </Box>
                    )}
                    {discountFundedBy === 'CompanyUpline' && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", mt: 0.5 }}>
                            Full agent discount of RM {(agentDiscount || 0).toFixed(2)} will be recorded as funded by company & upline
                        </Typography>
                    )}
                </Box>
            )}

            {/* Add-on Items Table */}
            <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    {fieldLabel('Add-on Items')}
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP }}>
                        Total: <strong style={{ color: '#0F172A' }}>RM {addonTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}</strong>
                    </Typography>
                </Box>

                {/* Table */}
                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', mb: 1 }}>
                    {/* Header */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 80px 28px', gap: 0, bgcolor: '#F8FAFC', px: 1, py: 0.5 }}>
                        {['Item', 'Qty', 'Unit Price', 'Total', ''].map((h, i) => (
                            <Typography key={i} sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: PP, textAlign: i >= 2 ? 'right' : 'left', pr: i === 3 ? 1 : 0 }}>
                                {h}
                            </Typography>
                        ))}
                    </Box>

                    {/* Rows */}
                    {(lineItems || []).length === 0 ? (
                        <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', fontFamily: PP, fontStyle: 'italic', textAlign: 'center', py: 1.5 }}>
                            No add-ons yet — pick from catalog below
                        </Typography>
                    ) : (lineItems || []).map((row, idx) => (
                        <Box key={idx} sx={{ display: 'grid', gridTemplateColumns: '1fr 60px 90px 80px 28px', gap: 0, alignItems: 'center', px: 1, py: 0.4, borderTop: '1px solid #F1F5F9', bgcolor: row.isBattery ? '#F0FDF4' : 'transparent' }}>
                            {/* Item name (+ base+rate structure caption for qty-priced add-ons) */}
                            <Box sx={{ minWidth: 0, pr: 0.5 }}>
                                <Typography sx={{ fontSize: '0.78rem', color: '#0F172A', fontFamily: PP }} noWrap title={row.itemName}>
                                    {row.itemName}
                                    {row.isBattery && <span style={{ marginLeft: 4, fontSize: '0.6rem', color: '#16A34A', fontWeight: 700 }}>BATT</span>}
                                </Typography>
                                {Number(row.priceRate) > 0 && (
                                    canEditAddons ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                            <span style={{ fontSize: '0.6rem', color: '#94A3B8', fontFamily: PP, whiteSpace: 'nowrap' }}>Base RM</span>
                                            <TextField
                                                size="small" type="number" value={row.priceBase ?? 0}
                                                onWheel={e => e.target.blur()}
                                                onChange={e => updateRow(idx, { priceBase: parseFloat(e.target.value) || 0 })}
                                                sx={{ width: 70, '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.68rem', py: '2px', px: '5px' }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                                                inputProps={{ min: 0, step: 0.01 }}
                                            />
                                            <span style={{ fontSize: '0.6rem', color: '#94A3B8', fontFamily: PP, whiteSpace: 'nowrap' }}>+ RM {Number(row.priceRate).toLocaleString()}/unit</span>
                                        </Box>
                                    ) : (
                                        <Typography sx={{ fontSize: '0.6rem', color: '#94A3B8', fontFamily: PP, lineHeight: 1.2 }} noWrap>
                                            RM {Number(row.priceBase || 0).toLocaleString()} base + RM {Number(row.priceRate).toLocaleString()}/unit
                                        </Typography>
                                    )
                                )}
                            </Box>
                            {/* Qty */}
                            {canEditAddons ? (
                                <TextField
                                    size="small" type="number" value={row.qty ?? 1}
                                    onWheel={e => e.target.blur()}
                                    // Allow qty 0 (e.g. backup-box removed) — a 0-qty row stays
                                    // visible to show the removed state, contributes 0 to the total,
                                    // and is hidden in the generated PDF.
                                    onChange={e => updateRow(idx, { qty: Math.max(0, parseInt(e.target.value) || 0) })}
                                    sx={{ '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.78rem', py: '3px', px: '6px', textAlign: 'center' }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                                    inputProps={{ min: 0, step: 1 }}
                                />
                            ) : (
                                <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontFamily: PP, textAlign: 'center' }}>{row.qty}</Typography>
                            )}
                            {/* Unit Price (= per-unit RATE for base+rate rows) */}
                            {canEditAddons ? (
                                <TextField
                                    size="small" type="number" value={row.unitPrice ?? 0}
                                    onWheel={e => e.target.blur()}
                                    onChange={e => updateRow(idx, Number(row.priceRate) > 0
                                        ? { priceRate: parseFloat(e.target.value) || 0 }
                                        : { unitPrice: parseFloat(e.target.value) || 0 })}
                                    sx={{ '& .MuiInputBase-input': { fontFamily: PP, fontSize: '0.78rem', py: '3px', px: '6px', textAlign: 'right' }, '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                            ) : (
                                <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontFamily: PP, textAlign: 'right', pr: 0.5 }}>
                                    {Number(row.unitPrice || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                                </Typography>
                            )}
                            {/* Line Total */}
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#0F172A', fontFamily: PP, textAlign: 'right', pr: 0.5 }}>
                                {Number(row.lineTotal || 0).toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                            </Typography>
                            {/* Remove */}
                            {canEditAddons ? (
                                <IconButton size="small" onClick={() => removeRow(idx)} sx={{ p: 0.25, color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}>
                                    <DeleteOutlineIcon sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                            ) : <span />}
                        </Box>
                    ))}

                    {/* Footer total */}
                    {(lineItems || []).length > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.5, borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC', gap: 1, alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP }}>Add-on Total</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#0F172A', fontFamily: PP, minWidth: 80, textAlign: 'right' }}>
                                RM {addonTotal.toLocaleString('en-MY', { minimumFractionDigits: 2 })}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Catalog picker + Battery picker — only shown to editors */}
                {canEditAddons && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {addonCatalog.length > 0 && (
                            <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                                <Select
                                    displayEmpty
                                    value=""
                                    onChange={e => { addFromCatalog(e.target.value); }}
                                    sx={{ fontFamily: PP, fontSize: '0.8rem' }}
                                    renderValue={() => <em style={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8' }}>+ Add from catalog…</em>}
                                >
                                    <MenuItem value="" disabled><em>Select an add-on</em></MenuItem>
                                    {addonCatalog.map(a => (
                                        <MenuItem key={a.addonId} value={a.addonId} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                            {a.itemName}
                                            {a.price ? <span style={{ marginLeft: 8, color: '#64748B', fontSize: '0.72rem' }}>RM {a.price}</span> : null}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {/* Battery picker — choose kWh (16..160), adds an ATAP-priced battery line */}
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select
                                displayEmpty
                                value={batteryKwhSel}
                                onChange={e => setBatteryKwhSel(e.target.value)}
                                sx={{ fontFamily: PP, fontSize: '0.8rem' }}
                                renderValue={(v) => v
                                    ? <span style={{ fontFamily: PP, fontSize: '0.8rem' }}>{v} kWh battery</span>
                                    : <em style={{ fontFamily: PP, fontSize: '0.8rem', color: '#94A3B8' }}>+ Battery (kWh)…</em>}
                            >
                                <MenuItem value="" disabled><em>Select battery kWh</em></MenuItem>
                                {batteryKwhOptions.map(k => (
                                    <MenuItem key={k} value={k} sx={{ fontFamily: PP, fontSize: '0.82rem' }}>
                                        {k} kWh <span style={{ marginLeft: 8, color: '#64748B', fontSize: '0.72rem' }}>RM {(Math.ceil(k / BATTERY_UNIT_KWH) * BATTERY_UNIT_PRICE).toLocaleString()}</span>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={!batteryKwhSel}
                            onClick={() => addBattery(batteryKwhSel)}
                            sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.78rem', borderRadius: '8px', borderColor: '#16A34A', color: '#16A34A', whiteSpace: 'nowrap' }}
                        >
                            Add Battery
                        </Button>
                    </Box>
                )}

                {/* Add-on Price: derived (greyed) when the table has items; otherwise
                    an editable lump-sum so old sales keep their existing amount. */}
                <Box sx={{ mt: 1 }}>
                    {fieldLabel('Add-on Price (RM)')}
                    {hasLineItems ? (
                        <TextField
                            size="small" fullWidth type="number"
                            value={addonTotal.toFixed(2)}
                            disabled
                            InputProps={{ sx: { ...inputSx, bgcolor: '#F8FAFC', color: '#64748B' }, inputProps: { step: 0.01, min: 0 } }}
                            helperText="Auto-computed from add-on table above"
                            FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }}
                        />
                    ) : (
                        <TextField
                            size="small" fullWidth type="number"
                            value={addOnPrice ?? ''}
                            onChange={e => setAddOnPrice && setAddOnPrice(e.target.value === '' ? null : (parseFloat(e.target.value) || 0))}
                            disabled={disablePricingFields}
                            onWheel={e => e.target.blur()}
                            InputProps={{ sx: inputSx, inputProps: { step: 0.01, min: 0 } }}
                            helperText="Lump-sum add-on amount (no add-on line items). Change only if needed."
                            FormHelperTextProps={{ sx: { fontFamily: PP, fontSize: '0.65rem', mx: 0 } }}
                        />
                    )}
                </Box>
            </Box>

            <Box sx={{ mb: 1.5 }}>
                {fieldLabel('Deposit Payment Date')}
                <DatePicker format="DD/MM/YYYY" disabled={disableDepositDate}
                    value={depositOn ? dayjs(convertDateTimeToInputDateString(depositOn)) : null}
                    onChange={(val) => setDepositOn(val ? val.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { size: 'small', fullWidth: true, InputProps: { sx: inputSx } } }} />
            </Box>

            {/* Payment-method pricing (the SAVED / quotation / commission basis). Uses
                the shared methodBreakdown so the create form, the edit form, the
                quotation PDF and the commission all tally exactly. Cash fields (Section
                1) above are the base; this converts system & add-on each ÷ factor
                (round-up RM10), discounts rounded, Final = Original − discounts. */}
            {Number(originalPrice) > 0 && (() => {
                const term = termForMethod(paymentMethod);   // 0 = cash, else CC term
                const isCC = term > 0;
                // Edit-in-place lens: inputs ARE the stored (method) basis → show them
                // DIRECTLY (no methodBreakdown, which would double-convert). Otherwise
                // (create / cash entry) convert cash → method as before.
                const lensBd = (() => {
                    const o = Number(originalPrice) || 0, as = Number(asternDiscount) || 0, ag = Number(agentDiscount) || 0, ad = Number(addOnPrice) || 0;
                    const f = Math.max(0, o - as - ag);
                    return { original: o, astern: as, agent: ag, final: f, addon: ad, total: f + ad };
                })();
                const cashBd = salesPriceLens ? null : methodBreakdown({ originalPrice, asternDiscount, agentDiscount, addOnPrice }, 0);
                const ccBd = salesPriceLens ? null : (isCC ? methodBreakdown({ originalPrice, asternDiscount, agentDiscount, addOnPrice }, term) : null);
                const row = (label, val, opts = {}) => {
                    const v = Number(val) || 0;
                    const isMarkup = opts.neg && v < 0;   // a negative discount = mark-up
                    const color = isMarkup ? '#16A34A' : (opts.neg ? '#B91C1C' : (opts.strong ? '#16A34A' : '#0F172A'));
                    return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: opts.strong ? '0.82rem' : '0.74rem', fontWeight: opts.strong ? 700 : 500, mt: opts.strong ? 0.4 : 0.15, pt: opts.strong ? 0.4 : 0, borderTop: opts.strong ? '1px solid #BFDBFE' : 'none' }}>
                        <span style={{ color: opts.strong ? '#0369A1' : '#475569' }}>{label}{isMarkup ? ' (mark-up)' : ''}</span>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color }}>{isMarkup ? '+ ' : (opts.neg ? '− ' : '')}RM {Math.abs(v).toLocaleString()}</span>
                    </Box>
                    );
                };
                const panel = (title, bd, saved) => (
                    <Box sx={{ flex: 1, p: 1.5, bgcolor: saved ? '#EFF6FF' : '#F8FAFC', border: '1px solid', borderColor: saved ? '#BFDBFE' : '#E2E8F0', borderRadius: 2, fontFamily: PP }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: saved ? '#1A8CFF' : '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.6 }}>
                            {title}{saved ? ' — saved / quotation / commission' : ' (reference)'}
                        </Typography>
                        {row('Original Price', bd.original)}
                        {row('Astern Discount', bd.astern, { neg: true })}
                        {row('Agent Discount', bd.agent, { neg: true })}
                        {row('Final Price', bd.final, { strong: true })}
                        {row('Add-on', bd.addon)}
                        {row('Total Quotation', bd.total, { strong: true })}
                    </Box>
                );
                // Always show CASH (tallies with the cash quotation line). When a CC term
                // is chosen, show its breakdown alongside — and mark IT as the saved basis.
                return (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: ccBd ? 'row' : 'column' }, gap: 1.5 }}>
                        {salesPriceLens && panel(isCC ? `${term}M Credit Card` : 'Cash', lensBd, true)}
                        {!salesPriceLens && panel('Cash', cashBd, !isCC)}
                        {ccBd && panel(`${term}M Credit Card`, ccBd, true)}
                    </Box>
                );
            })()}

            {/* SAVED pricing (Section 2) — shown when editing an existing sale and the
                cash Section 1 is left blank (untouched). These are the method-basis
                numbers stored at create; they REMAIN visible so the sale's payment-method
                pricing doesn't disappear on edit. Editing Section 1 above swaps to the
                live (recomputed) Section 2. */}
            {savedPricing && !(Number(originalPrice) > 0) && (() => {
                const sOrig = Number(savedPricing.originalPrice) || 0;
                const sAstern = Number(savedPricing.asternDiscount) || 0;
                const sAgent = Number(savedPricing.agentDiscount) || 0;
                const sAddon = Number(savedPricing.addOnPrice) || 0;
                const sFinal = (savedPricing.finalPrice != null)
                    ? Number(savedPricing.finalPrice)
                    : Math.max(0, sOrig - sAstern - sAgent);
                const sTotal = sFinal + sAddon;
                if (sTotal <= 0 && sOrig <= 0) return null;
                const pm = String(savedPaymentMethod || '').toLowerCase();
                const isCC = pm.includes('12') || pm.includes('24') || pm.includes('36') || pm.includes('60');
                const term = pm.includes('60') ? 60 : pm.includes('36') ? 36 : pm.includes('24') ? 24 : pm.includes('12') ? 12 : 0;
                const row = (label, val, opts = {}) => {
                    const v = Number(val) || 0;
                    const isMarkup = opts.neg && v < 0;   // a negative discount = mark-up
                    const color = isMarkup ? '#16A34A' : (opts.neg ? '#B91C1C' : (opts.strong ? '#16A34A' : '#0F172A'));
                    return (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: opts.strong ? '0.82rem' : '0.74rem', fontWeight: opts.strong ? 700 : 500, mt: opts.strong ? 0.4 : 0.15, pt: opts.strong ? 0.4 : 0, borderTop: opts.strong ? '1px solid #BFDBFE' : 'none' }}>
                        <span style={{ color: opts.strong ? '#0369A1' : '#475569' }}>{label}{isMarkup ? ' (mark-up)' : ''}</span>
                        <span style={{ fontFamily: "'JetBrains Mono',monospace", color }}>{isMarkup ? '+ ' : (opts.neg ? '− ' : '')}RM {Math.abs(v).toLocaleString()}</span>
                    </Box>
                    );
                };
                return (
                    <Box sx={{ mt: 2, p: 1.5, bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 2, fontFamily: PP }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#1A8CFF', textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.6 }}>
                            Saved Pricing · {isCC ? `${term}M Credit Card` : 'Cash'} — current saved / quotation / commission
                        </Typography>
                        {row('Original Price', sOrig)}
                        {row('Astern Discount', sAstern, { neg: true })}
                        {row('Agent Discount', sAgent, { neg: true })}
                        {row('Final Price', sFinal, { strong: true })}
                        {row('Add-on', sAddon)}
                        {row('Total Quotation', sTotal, { strong: true })}
                        <Typography sx={{ fontSize: '0.62rem', color: '#64748B', mt: 0.5 }}>This is the saved figure. To change it, enter a new cash price in Section 1 above.</Typography>
                    </Box>
                );
            })()}
        </>
    );
}
